<template>
  <Toolbar class="p-mb-4 p-mt-4">
    <template #start>
      <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('rolesAndPermissions') }}</div>
      <AddNewButton v-if="!cannotManageAccess"
                    :disabled="isAddDisabled"
                    :label="$t('Add')"
                    @click="addNewItem"/>
    </template>
  </Toolbar>

  <Spinner v-if="user && isLoading"/>
  <DesktopDataTable v-else
                    :dataTableIsLoading="dataTableIsLoading"
                    :itemsArrayIsEmpty="itemsArrayIsEmpty"
                    :sort="sort"
                    :showSearchInput="false"
                    :actionButtonsCount="actionButtonsCount"
                    @sort-items="sortItems"
                    :headers="[
                        {name: 'location_id', title: $t('Location'), sortable: false},
                        {name: 'role', title: $t('Role'), sortable: false},
                        {
                          name: 'additional_permissions',
                          title: $t('additionalPermissions'),
                          isHidden: !thereIsServiceAdvisor,
                          width: '35%',
                          sortable: false},
                        {name: 'created_at', title: $t('Created'), width: '17%',sortable: true},
                    ]">
    <template v-slot:body>
      <tr v-for="item of items" :key="item.id"
          :class="{'updated-item': isItemUpdated(item.id)}">

        <td class="table-data">
          <span v-if="item.location_id && item.location?.public_name">{{ item.location.public_name }}</span>
          <i v-else class="ti-minus"/>
        </td>

        <td class="table-data">
<!--          <span v-if="item.role">{{ roleLabel(item.role) }}</span>-->
          <UserRoleBadge v-if="item.role" :role="item.role"/>
          <i v-else class="ti-minus"/>
        </td>

        <td v-if="thereIsServiceAdvisor">
          <template v-if="isServiceAdvisor(item)">
            <div class="p-d-flex p-ai-center" >
              <Checkbox :id="`can_send_estimate_pdf_${item.id}`"
                        :class="{'no-pointer': isRowActionDisabled(item)}"
                        :binary="true"
                        v-model="item.can_send_estimate_pdf"
                        :disabled="isRowActionDisabled(item)"
                        @change="updateAssignmentFlag(item, 'can_send_estimate_pdf')"/>
              <div>
                <label :for="`can_send_estimate_pdf_${item.id}`">
                  <div class="pointer p-p-1"
                       :class="{'no-pointer': isRowActionDisabled(item)}">
                    {{ $t('canSendEstimatePdf') }}
                  </div>
                </label>
              </div>
            </div>

            <div class="p-d-flex p-ai-center p-mt-2">
              <Checkbox :id="`can_view_own_sales_statistics_${item.id}`"
                        :class="{'no-pointer': isRowActionDisabled(item)}"
                        :binary="true"
                        v-model="item.can_view_own_sales_statistics"
                        :disabled="isRowActionDisabled(item)"
                        @change="updateAssignmentFlag(item, 'can_view_own_sales_statistics')"/>
              <div>
                <label :for="`can_view_own_sales_statistics_${item.id}`">
                  <div class="pointer p-p-1"
                       :class="{'no-pointer': isRowActionDisabled(item)}">
                    {{ $t('canViewOwnSalesStatistics') }}
                  </div>
                </label>
              </div>
            </div>
          </template>

          <i v-else class="ti-minus"/>
        </td>

        <td class="table-date-line">
          <span v-if="item.created_at">
            {{ formatBy(item.created_at, item.creator) }}
          </span>
          <i v-else class="ti-minus"/>
        </td>

        <td v-show="actionButtonsCount">
          <div class="actions">
            <DeleteButton @click="confirmItemDelete(item)"
                          :disabled="isRowActionDisabled(item)"/>
          </div>
        </td>
      </tr>
    </template>
  </DesktopDataTable>

  <OwnPaginator v-show="!itemsArrayIsEmpty"
                :rows="pagination.perPage"
                :currentPage="pagination.currentPage - 1"
                :totalPages="pagination.pageCount"
                :totalRecords="pagination.totalCount"
                :rowsPerPageOptions="[5,10,25,50,100,200]"
                :pageLinkSize="11"/>

  <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>


  <UserAssignmentModal :visible="itemModal"
                       :item="item"
                       :userId="user?.id"
                       :locations="locations"
                       :accessMap="effectiveAccessMap"
                       @close="closeItemModal"
                       @update-items="updateItems"/>

  <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                      :modalDataIsLoading="modalDataIsLoading"
                      @close="closeConfirmItemDeleteModal"
                      @delete-item="onDeleteItem"/>
</template>

<script>
import httpClient from '@/services/http.services'
import httpMixins from "@/mixins/httpMixins";
import datatableMixins from "@/mixins/datatableMixins";
import formatMixins from "@/mixins/formatMixins";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import UserAssignmentModal from "@/pages/employees/components/UserAssignmentModal.vue";
import constants from "@/constants";
import settings from "@/settings";
import UserRoleBadge from "@/components/UserRoleBadge.vue";
import billingRestrictionsMixin from '@/mixins/billingRestrictionsMixin'

export default {
  mixins: [ showErrorsMixins, datatableMixins, formatMixins, httpMixins, billingRestrictionsMixin ],
  components: { UserAssignmentModal, UserRoleBadge },
  emits: ['update-items', 'updateItems'],
  props: {
    user: {
      type: Object,
      default: null,
    },
    employeeIsLoading: {
      type: Boolean,
      default: false
    },
  },
	data() {
		return {
      fieldsExpand: 'creator,location',
      apiLink: 'user-access/get-accesses',
      deleteItemUrl: 'user-access/delete-user-assignment',
      sort: {
        column: 'id',
        direction: '-',
      },
      locations: [],
		}
	},
  watch: {
    '$route'() {
      const route = this.$route.path.split('/')
      if (this.$route.path === '/login' || route.indexOf('employees') === -1 || route.indexOf('roles') === -1) {
        return false
      }
      this.checkRouteQuery()
      this.getItems()
    },
  },
  mounted() {
    if (!this.$route.params.uid) return
    this.checkRouteQuery()
    this.$store.commit('SET_FETCH_IN_FLIGHT', false)
    this.getPageData()
  },
  methods: {
    async onDeleteItem() {
      const res = await this.deleteItem()

      if (res?.access_map) {
        this.$store.commit('setAccessMap', res.access_map)
      }
    },
    getPageData() {
      const userId = this.user?.id
      if (!userId) return

      this.getItems()
      this.getLocations()
    },
    async getItems(showSpinner = false) {
      const userId = this.user?.id
      if (!userId || !this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      this.disableEditButton = true
      if (showSpinner) {
        this.isLoading = true
      }
      const ctrl = this.setController?.('employeeUserAccess')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          method: 'post',
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          data: {
            userId: userId,
            dateFrom: this.fromTimestamp,
            dateTo: this.toTimestamp,
            includeArchived: this.employeeIsArchived ? 1 : 0,
          },
          signal: ctrl?.signal,
        })

        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }


          this.items = data.map(item => ({
            ...item,
            can_send_estimate_pdf: !!+item.can_send_estimate_pdf,
            can_view_own_sales_statistics: !!+item.can_view_own_sales_statistics,
          }))

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        this.disableEditButton = false
        if (ctrl && this._abortors?.employeeUserAccess === ctrl) {
          this._abortors.employeeUserAccess = null
        }
      }
    },
    isRowActionDisabled(item) {
      return this.dataTableIsLoading ||
          this.employeeIsLoading ||
          this.billingDataEditingLocked ||
          this.cannotManageAccess ||
          !!item?._savingFlag
    },
    async updateAssignmentFlag(item, field) {
      if (this.billingActionIsRestricted()) return

      const allowedFields = [
        'can_send_estimate_pdf',
        'can_view_own_sales_statistics',
      ]

      if (!allowedFields.includes(field) || !item?.id || item._savingFlag) return

      const oldValue = !item[field]
      item._savingFlag = true

      try {
        const { data } = await httpClient.post(`user-access/update-flag?id=${item.id}`, {
          field,
          value: item[field] ? 1 : 0,
        })

        if (data?.success) {
          item[field] = !!data.value

          if (data.access_map) {
            this.$store.commit('setAccessMap', data.access_map)
          }

          this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife})

          return
        }

        item[field] = oldValue
        this.showUnknownError()
      } catch (err) {
        item[field] = oldValue
        this.showError(err)
      } finally {
        item._savingFlag = false
      }
    },
    isServiceAdvisor(item) {
      return +item.role === constants.userRoles.moderator
    },
	},
  computed: {
    thereIsServiceAdvisor() {
      return this.items?.some(item => +item.role === constants.userRoles.moderator)
    },
    isAddDisabled() {
      return this.employeeIsLoading ||
          this.dataTableIsLoading ||
          this.billingDataEditingLocked
    },
    // cannotManageAccess() {
    //   const targetRole = Number(this.user?.max_role_with_archived || 0)
    //   const currentRole = Number(this.$store.getters.effectiveRole || 0)
    //   const roles = constants.userRoles
    //
    //   if (!currentRole) return true
    //
    //   if (this.employeeIsArchived && !this.isOwnerOrSuperAdmin) return true
    //
    //   if (currentRole === roles.superAdmin) return false
    //
    //   if (currentRole === roles.owner) {
    //     return targetRole === roles.superAdmin
    //   }
    //
    //   if (!targetRole) {
    //     return ![roles.admin].includes(currentRole)
    //   }
    //
    //   if (currentRole === roles.admin) {
    //     return ![
    //       roles.moderator,
    //       roles.employeeWorkshopMaster,
    //       roles.employeeMaster,
    //     ].includes(targetRole)
    //   }
    //
    //   return true
    // },

    cannotManageAccess() {
      const targetRole = Number(this.user?.max_role_with_archived || 0)
      const currentRole = Number(this.$store.getters.effectiveRole || 0)
      const roles = constants.userRoles

      if (!currentRole) return true

      if (this.employeeIsArchived && !this.isOwnerOrSuperAdmin) return true

      if (currentRole === roles.superAdmin) return false

      if (currentRole === roles.owner) {
        return targetRole === roles.superAdmin
      }

      if (!targetRole) {
        return ![
          roles.admin,
          roles.accountant,
        ].includes(currentRole)
      }

      if (
          currentRole === roles.admin ||
          currentRole === roles.accountant
      ) {
        return ![
          roles.moderator,
          roles.employeeWorkshopMaster,
          roles.employeeMaster,
        ].includes(targetRole)
      }

      return true
    },

    isOwnerOrSuperAdmin() {
      const role = Number(this.$store.getters.effectiveRole || 0)
      const roles = constants.userRoles

      return [roles.superAdmin, roles.owner].includes(role)
    },

    employeeIsArchived() {
      return !!this.user?.employee && Number(this.user.employee.status) !== 1
    },
    effectiveAccessMap() {
      return this.employeeIsArchived
          ? (this.user?.archived_access_map || [])
          : (this.user?.access_map || [])
    },
    actionButtonsCount() {
      return !this.cannotManageAccess ? 1 : 0
    }
  }
}
</script>

<style scoped lang="scss">
td {
  height: 44px;
}
</style>